/**
 * ISO 4217 currencies that PatronEZ supports end-to-end (deal amounts,
 * billing display, FX). Symbols are the locale-neutral Unicode form
 * Intl.NumberFormat falls back to when style: 'currency' fails to
 * resolve a glyph for the user's locale — pre-baking them so the
 * dropdown shows a real currency mark without a Number.format() call
 * for every row.
 */
export interface CurrencyMeta {
  code: string;
  name: string;
  symbol: string;
  aliases?: string[];
}

export const ISO_CURRENCIES: CurrencyMeta[] = [
  { code: 'JPY', name: 'Japanese yen',        symbol: '¥',   aliases: ['yen', '円'] },
  { code: 'USD', name: 'US dollar',           symbol: '$',   aliases: ['dollar', 'us'] },
  { code: 'EUR', name: 'Euro',                symbol: '€',   aliases: ['euro'] },
  { code: 'GBP', name: 'British pound',       symbol: '£',   aliases: ['pound', 'sterling'] },
  { code: 'CAD', name: 'Canadian dollar',     symbol: 'C$' },
  { code: 'AUD', name: 'Australian dollar',   symbol: 'A$' },
  { code: 'CHF', name: 'Swiss franc',         symbol: 'CHF' },
  { code: 'CNY', name: 'Chinese yuan',        symbol: '¥',   aliases: ['rmb', 'renminbi', '人民幣'] },
  { code: 'HKD', name: 'Hong Kong dollar',    symbol: 'HK$' },
  { code: 'SGD', name: 'Singapore dollar',    symbol: 'S$' },
  { code: 'KRW', name: 'South Korean won',    symbol: '₩',   aliases: ['won', '원'] },
  { code: 'TWD', name: 'New Taiwan dollar',   symbol: 'NT$' },
  { code: 'INR', name: 'Indian rupee',        symbol: '₹',   aliases: ['rupee'] },
  { code: 'IDR', name: 'Indonesian rupiah',   symbol: 'Rp' },
  { code: 'THB', name: 'Thai baht',           symbol: '฿' },
  { code: 'PHP', name: 'Philippine peso',     symbol: '₱' },
  { code: 'MYR', name: 'Malaysian ringgit',   symbol: 'RM' },
  { code: 'VND', name: 'Vietnamese dong',     symbol: '₫' },
  { code: 'BRL', name: 'Brazilian real',      symbol: 'R$' },
  { code: 'MXN', name: 'Mexican peso',        symbol: 'Mex$' },
  { code: 'ARS', name: 'Argentine peso',      symbol: 'AR$' },
  { code: 'CLP', name: 'Chilean peso',        symbol: 'CL$' },
  { code: 'ZAR', name: 'South African rand',  symbol: 'R' },
  { code: 'AED', name: 'UAE dirham',          symbol: 'د.إ' },
  { code: 'SAR', name: 'Saudi riyal',         symbol: 'SR' },
  { code: 'TRY', name: 'Turkish lira',        symbol: '₺' },
  { code: 'PLN', name: 'Polish zloty',        symbol: 'zł' },
  { code: 'SEK', name: 'Swedish krona',       symbol: 'kr' },
  { code: 'NOK', name: 'Norwegian krone',     symbol: 'kr' },
  { code: 'DKK', name: 'Danish krone',        symbol: 'kr' },
  { code: 'CZK', name: 'Czech koruna',        symbol: 'Kč' },
  { code: 'HUF', name: 'Hungarian forint',    symbol: 'Ft' },
  { code: 'RUB', name: 'Russian ruble',       symbol: '₽' },
  { code: 'NZD', name: 'New Zealand dollar',  symbol: 'NZ$' },
  { code: 'ILS', name: 'Israeli new shekel',  symbol: '₪' },
  // --- additional currencies for full coverage ---
  { code: 'COP', name: 'Colombian Peso', symbol: 'COL$' },
  { code: 'PEN', name: 'Peruvian Sol', symbol: 'S/.' },
  { code: 'UYU', name: 'Uruguayan Peso', symbol: '$U' },
  { code: 'PYG', name: 'Paraguayan Guarani', symbol: '₲' },
  { code: 'DOP', name: 'Dominican Peso', symbol: 'RD$' },
  { code: 'ISK', name: 'Icelandic Króna', symbol: 'kr' },
  { code: 'RON', name: 'Romanian Leu', symbol: 'lei' },
  { code: 'BGN', name: 'Bulgarian Lev', symbol: 'лв' },
  { code: 'HRK', name: 'Croatian Kuna', symbol: 'kn' },
  { code: 'RSD', name: 'Serbian Dinar', symbol: 'дин' },
  { code: 'UAH', name: 'Ukrainian Hryvnia', symbol: '₴' },
  { code: 'GEL', name: 'Georgian Lari', symbol: '₾' },
  { code: 'KHR', name: 'Cambodian Riel', symbol: '៛' },
  { code: 'BDT', name: 'Bangladeshi Taka', symbol: '৳' },
  { code: 'PKR', name: 'Pakistani Rupee', symbol: '₨' },
  { code: 'LKR', name: 'Sri Lankan Rupee', symbol: 'Rs' },
  { code: 'KZT', name: 'Kazakhstani Tenge', symbol: '₸' },
  { code: 'UZS', name: 'Uzbekistani Som', symbol: 'soʻm' },
  { code: 'QAR', name: 'Qatari Riyal', symbol: 'ر.ق' },
  { code: 'KWD', name: 'Kuwaiti Dinar', symbol: 'د.ك' },
  { code: 'BHD', name: 'Bahraini Dinar', symbol: '.د.ب' },
  { code: 'OMR', name: 'Omani Rial', symbol: 'ر.ع' },
  { code: 'JOD', name: 'Jordanian Dinar', symbol: 'د.ا' },
  { code: 'LBP', name: 'Lebanese Pound', symbol: 'ل.ل' },
  { code: 'EGP', name: 'Egyptian Pound', symbol: 'ج.م' },
  { code: 'MAD', name: 'Moroccan Dirham', symbol: 'DH' },
  { code: 'NGN', name: 'Nigerian Naira', symbol: '₦' },
  { code: 'KES', name: 'Kenyan Shilling', symbol: 'KSh' },
  { code: 'GHS', name: 'Ghanaian Cedi', symbol: 'GH₵' },
  { code: 'TZS', name: 'Tanzanian Shilling', symbol: 'TSh' },
];

export function currencyByCode(code: string): CurrencyMeta | undefined {
  const c = code.toUpperCase();
  return ISO_CURRENCIES.find((x) => x.code === c);
}
